import { apiClient, signApiRequest } from ".";
import { useMutation } from "@tanstack/react-query";
import { CustomerCredentials } from "../ui/customerDataStore";
import {
  BankIdSignedData,
  IdentityVerificationId,
  UserOrigin,
} from "../../types";
import { captureException } from "@sentry/react";

export const createUser = async ({
  signedData,
  email,
  phone,
  agentId,
  identityVerificationId,
  transactionId,
  credentials,
  origin,
}: {
  signedData: BankIdSignedData;
  email: string;
  phone: string;
  agentId: string | null;
  identityVerificationId?: IdentityVerificationId;
  transactionId?: string;
  credentials: CustomerCredentials;
  origin: UserOrigin;
}) => {
  const givenName =
    signedData?.providerInfo?.seBankID?.completionData?.user?.givenName;
  const surname =
    signedData?.providerInfo?.seBankID?.completionData?.user?.surname;
  const personalNumber =
    signedData?.providerInfo?.seBankID?.completionData?.user?.personalNumber;

  if (!givenName || !surname || !personalNumber) {
    throw new Error("Missing required user information");
  }

  const userObject = {
    user: {
      email,
      phone,
      agentId,
      firstName: givenName,
      lastName: surname,
      ssn: personalNumber,
      birthDate: `${personalNumber.substring(0, 4)}-${personalNumber.substring(4, 6)}-${personalNumber.substring(6, 8)}T00:00:00Z`,
      fid: credentials.identityId,
      origin,
    },
    identityVerificationId,
    transactionId, // when identityVerificationId is null (CAS was down during auth).
  };

  const signedRequest = await signApiRequest(
    "POST",
    `${import.meta.env.VITE_API_URL}/user/user`,
    credentials,
    { "content-type": "application/json" },
    userObject,
  );

  const response = await apiClient(signedRequest);
  return response.data;
};

export const useCreateUser = () =>
  useMutation({
    mutationFn: ({
      signedData,
      email,
      phone,
      agentId,
      identityVerificationId,
      transactionId,
      credentials,
      origin,
    }: {
      signedData: BankIdSignedData;
      email: string;
      phone: string;
      agentId: string | null;
      identityVerificationId?: IdentityVerificationId;
      transactionId?: string;
      credentials: CustomerCredentials;
      origin: UserOrigin;
    }) => {
      return createUser({
        signedData,
        email,
        phone,
        agentId,
        identityVerificationId,
        transactionId,
        credentials,
        origin,
      });
    },
    onSuccess: (data) => {
      return data;
    },
    onError: (error) => {
      captureException(error, {
        tags: {
          operation: "createUser",
          errorType: error.name || "UnknownError",
        },
        extra: {
          apiEndpoint: "/user/user",
        },
        level: "error",
      });
    },
  });
